import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IntegrationPage, toMessage } from "../components/integration-shell";
import { ApiUnavailableState, EmptyState, LoadingState } from "../components/states";
import {
  SafeTestPanel,
  StatusPill,
  StreamHistory,
  ValidationFindings,
  PROVIDER_UNVALIDATED_NOTE,
} from "../components/integrations";
import {
  api,
  type IntegrationConfigValidation,
  type SafeIntegration,
  type SafeTestResult,
  type StreamEventView,
} from "../lib/api";

interface StreamDraft {
  identifier: string; // broadcasterId (Twitch) or channelId (YouTube)
  displayName: string;
  targetGuildId: string;
  targetChannelKey: string;
  notificationTemplate: string;
}

function readDraft(type: string, config: unknown): StreamDraft {
  const c = (config ?? {}) as Record<string, unknown>;
  const str = (k: string) => (typeof c[k] === "string" ? (c[k] as string) : "");
  return {
    identifier: type === "TWITCH" ? str("broadcasterId") : str("channelId"),
    displayName: str("displayName"),
    targetGuildId: str("targetGuildId"),
    targetChannelKey: str("targetChannelKey"),
    notificationTemplate: str("notificationTemplate"),
  };
}

function toConfig(type: string, d: StreamDraft): Record<string, unknown> {
  const base = {
    displayName: d.displayName,
    targetGuildId: d.targetGuildId,
    targetChannelKey: d.targetChannelKey,
    notificationTemplate: d.notificationTemplate,
    enabled: false,
  };
  return type === "TWITCH"
    ? { ...base, broadcasterId: d.identifier, notifyOnLiveStart: true, notifyOnUpdate: false, cooldownSeconds: 1800 }
    : { ...base, channelId: d.identifier, pollIntervalSeconds: 300, mode: "live-only" };
}

/**
 * Twitch + YouTube setup (section 29). A provider is shown as "connected" ONLY
 * after validation succeeds AND the integration reaches HEALTHY — never on a
 * draft. The test action is SAFE (validate + preview only). Duplicate suppression
 * and provider health are surfaced read-only.
 */
export function StreamingSetup({
  integration,
  events,
  validation,
  testResult,
  busy,
  message,
  onSaveValidate,
  onLoadHistory,
  onTest,
}: {
  integration: SafeIntegration;
  events: StreamEventView[] | null;
  validation: IntegrationConfigValidation | null;
  testResult: SafeTestResult | null;
  busy?: boolean;
  message?: string | null;
  onSaveValidate: (config: Record<string, unknown>) => void;
  onLoadHistory: () => void;
  onTest: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState<StreamDraft>(() => readDraft(integration.type, integration.configuration));
  const set = <K extends keyof StreamDraft>(k: K, v: StreamDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  // "Connected" requires a passed validation AND a healthy provider status.
  const connected = validation?.valid === true && integration.status === "HEALTHY";
  const providerLabel = integration.type === "TWITCH" ? "Twitch" : "YouTube";

  return (
    <div className="ob-builder">
      <div className="row" style={{ alignItems: "center", gap: "0.75rem" }}>
        <h4 className="section-title" style={{ margin: 0 }}>
          {providerLabel} provider
        </h4>
        {connected ? <StatusPill tone="ok" label="Connected" /> : <StatusPill tone="neutral" label="Not connected" />}
      </div>
      {!connected ? <p className="muted">{PROVIDER_UNVALIDATED_NOTE}</p> : null}

      <div className="world-panel world-panel--proposed">
        <div className="card-grid">
          <label className="field">
            <span className="field__label">{integration.type === "TWITCH" ? "Broadcaster id" : "Channel id"}</span>
            <input className="field__input" value={draft.identifier} onChange={(e) => set("identifier", e.target.value)} aria-label="Provider identifier" />
          </label>
          <label className="field">
            <span className="field__label">Display name</span>
            <input className="field__input" value={draft.displayName} onChange={(e) => set("displayName", e.target.value)} aria-label="Stream display name" />
          </label>
          <label className="field">
            <span className="field__label">Target guild id</span>
            <input className="field__input" value={draft.targetGuildId} onChange={(e) => set("targetGuildId", e.target.value)} aria-label="Stream target guild id" />
          </label>
          <label className="field">
            <span className="field__label">Target channel key</span>
            <input className="field__input" value={draft.targetChannelKey} onChange={(e) => set("targetChannelKey", e.target.value)} aria-label="Stream target channel key" />
          </label>
        </div>
        <label className="field">
          <span className="field__label">Notification template</span>
          <textarea className="field__input" rows={2} value={draft.notificationTemplate} onChange={(e) => set("notificationTemplate", e.target.value)} aria-label="Stream notification template" />
        </label>
        <div className="row" style={{ gap: "0.5rem", marginTop: "0.5rem" }}>
          <button type="button" className="btn btn--primary" disabled={busy || draft.identifier.length === 0} onClick={() => onSaveValidate(toConfig(integration.type, draft))}>
            {busy ? "Working…" : "Save & validate identifier"}
          </button>
          <button type="button" className="btn btn--secondary" disabled={busy} onClick={onLoadHistory}>
            Load live-session history
          </button>
        </div>
        {validation ? <ValidationFindings validation={validation} /> : null}
        {message ? <p className="muted" role="status">{message}</p> : null}
      </div>

      <SafeTestPanel result={testResult} onRunTest={onTest} busy={busy} />

      <div className="world-panel world-panel--current">
        <h4 className="section-title" style={{ marginTop: 0 }}>
          Live-session history &amp; duplicate suppression
        </h4>
        <p className="muted">Duplicate suppression is enforced server-side; only distinct live sessions are notified once.</p>
        {events === null ? <p className="muted">Load history to view recorded live sessions.</p> : <StreamHistory events={events} />}
      </div>
    </div>
  );
}

export function IntegrationStreaming(): JSX.Element {
  return (
    <IntegrationPage title="Streaming setup (Twitch / YouTube)" intro={<p>Validate a streaming identifier and preview notifications safely.</p>}>
      {(tenantId) => <StreamingPanel tenantId={tenantId} />}
    </IntegrationPage>
  );
}

function StreamingPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const [params] = useSearchParams();
  const [list, setList] = useState<SafeIntegration[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(params.get("id"));
  const [integration, setIntegration] = useState<SafeIntegration | null>(null);
  const [events, setEvents] = useState<StreamEventView[] | null>(null);
  const [validation, setValidation] = useState<IntegrationConfigValidation | null>(null);
  const [testResult, setTestResult] = useState<SafeTestResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.integrations
      .list(tenantId)
      .then((r) => {
        const streams = r.integrations.filter((i) => i.type === "TWITCH" || i.type === "YOUTUBE");
        setList(streams);
        if (!selectedId && streams[0]) setSelectedId(streams[0].id);
      })
      .catch((e) => setError(toMessage(e)));
  }, [tenantId]);

  useEffect(() => {
    if (!selectedId) return;
    api.integrations
      .get(tenantId, selectedId)
      .then((r) => {
        setIntegration(r.integration);
        setValidation(null);
        setEvents(null);
        setTestResult(null);
      })
      .catch((e) => setError(toMessage(e)));
  }, [tenantId, selectedId]);

  const saveValidate = async (config: Record<string, unknown>) => {
    if (!integration) return;
    setBusy(true);
    setMessage(null);
    try {
      await api.integrations.updateConfiguration(tenantId, integration.id, config);
      const r = await api.integrations.validate(tenantId, integration.id);
      setValidation(r.validation);
      setIntegration(r.integration);
      setMessage(r.validation.valid ? "Identifier configuration validated." : `Validation found ${r.validation.errors.length} issue(s).`);
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const loadHistory = async () => {
    if (!integration) return;
    setBusy(true);
    try {
      const r = await api.integrations.getStream(tenantId, integration.id);
      setEvents(r.events);
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doTest = async () => {
    if (!integration) return;
    setBusy(true);
    try {
      const r = await api.integrations.test(tenantId, integration.id);
      setTestResult(r);
      setMessage(r.note);
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ApiUnavailableState detail={error} />;
  if (list === null) return <LoadingState label="Reading streaming integrations…" />;

  return (
    <div className="ob-builder">
      <label className="field">
        <span className="field__label">Streaming integration</span>
        <select className="field__input" value={selectedId ?? ""} onChange={(e) => setSelectedId(e.target.value || null)} aria-label="Select streaming integration">
          <option value="">Select a Twitch or YouTube integration…</option>
          {list.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name} ({i.type}, {i.status})
            </option>
          ))}
        </select>
      </label>

      {integration ? (
        <StreamingSetup
          integration={integration}
          events={events}
          validation={validation}
          testResult={testResult}
          busy={busy}
          message={message}
          onSaveValidate={saveValidate}
          onLoadHistory={loadHistory}
          onTest={doTest}
        />
      ) : (
        <EmptyState title="No streaming integration selected">Create a Twitch or YouTube integration on the Integrations page first.</EmptyState>
      )}
    </div>
  );
}
